import type { MouseEvent, MutableRefObject, RefObject } from "react"
import { useEffect, useRef, useCallback } from "react"
import { isAddress } from "@ethersproject/address"
import { BigNumber } from "@ethersproject/bignumber"
import { select } from "d3-selection"
import type { HierarchyCircularNode } from "d3-hierarchy"
import { pack, hierarchy } from "d3-hierarchy"
import type { InternMap } from "d3-array"
import { rollup, sum, map } from "d3-array"
import type { D3ZoomEvent } from "d3-zoom"
import { zoom, zoomIdentity } from "d3-zoom"

import type { AccountId, Vote } from "query/graphql"
import type { VoteStyle } from "common/helpers/voteStyle"
import { shortSolAddress, shortString } from "common/helpers/string"
import { bigNumberToNumber } from "common/helpers/bignumber"
import { isSolanaAddress } from "web3/helpers/address"

type Props = {
  dataset: Vote[]
  governanceId?: AccountId
  decimals: number
  voteStyles: Record<string, VoteStyle>
  onClickVoterBubble?: (address: string) => void
}

type Values = {
  ref: MutableRefObject<null | SVGSVGElement>
  buttonRef: RefObject<HTMLButtonElement>
  zoomInBtn: RefObject<HTMLButtonElement>
  zoomOutBtn: RefObject<HTMLButtonElement>
}

export const useD3BubbleChart = ({
  dataset,
  decimals,
  voteStyles,
  onClickVoterBubble,
}: Props): Values => {
  const ref = useRef<SVGSVGElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const zoomInBtn = useRef<HTMLButtonElement>(null)
  const zoomOutBtn = useRef<HTMLButtonElement>(null)

  const handleVoterClick = useCallback(
    (event: MouseEvent<SVGSVGElement>) => {
      // @ts-expect-error FIXME: Add types to this __data__ key
      const voter = event.target.__data__?.parent?.data?.[0]
      const { address } = voter

      if (onClickVoterBubble) {
        onClickVoterBubble(address)
      }
    },
    [onClickVoterBubble],
  )

  const clipText = (
    d: HierarchyCircularNode<Vote>,
    t: string,
    scale: number,
  ) => {
    let name = t.substring(0, d.r / scale)

    if (name.length < t.length) {
      name = name.substring(0, name.length - Math.min(2, name.length)) + "..."
    }

    return name
  }

  const getUsername = (d: HierarchyCircularNode<Vote>, dataset: Vote[]) => {
    // @ts-expect-error correct typings
    const [voter] = d.parent.data
    const { address: voterAddress } = voter

    const vote = dataset.find((vote) => vote.voter.address === voterAddress)

    if (!vote) return "0x0"

    const {
      voter: { name, address },
    } = vote

    const displayName = Boolean(name) ? name : address

    // If EVM address
    if (isAddress(displayName)) {
      return shortString(displayName)
    }

    // If Solana address
    if (isSolanaAddress(displayName)) {
      return shortSolAddress(displayName)
    }

    return name
  }

  useEffect(() => {
    const element = ref.current

    if (element === null) return

    const svgElement = select(element)
    svgElement.selectAll("*").remove()

    const diameter = 720

    const width = diameter
    const height = 600

    svgElement
      .attr("width", width)
      .attr("height", height)
      .attr("preserveAspectRatio", "xMinYMin meet")
      .attr("class", "bubble")

    const container = svgElement.append("g")

    const rollupReducerFn = (dataset: Vote[]) => {
      return sum(dataset, ({ amount: w }) => {
        const bigWeight = BigNumber.from(w)
        const weight = bigNumberToNumber(bigWeight, decimals)

        return weight
      })
    }
    const rollupVoterFn = ({ voter }: Vote) => voter
    const rollupSupportFn = ({ type }: Vote) => type
    const rollupData = rollup(
      dataset,
      rollupReducerFn,
      rollupVoterFn,
      rollupSupportFn,
    )

    const bubblesHierarchyChildrenAccessorFn = ([_, weight]: InternMap<
      string,
      number
    >) => {
      return weight
    }

    const bubblesPack = pack().size([width, height]).padding(1.5)
    const bubblesHierarchy = hierarchy(
      // @ts-expect-error correct typings
      rollupData,
      bubblesHierarchyChildrenAccessorFn,
    )
      // @ts-expect-error correct typings
      .sum(([_, weight]) => {
        return weight
      })

    const data = bubblesPack(
      bubblesHierarchy,
    ).leaves() as HierarchyCircularNode<Vote>[]

    const bubbles = container
      .selectAll(".node")
      .data(data)
      .enter()
      .append("g")
      .attr("class", "node")
      .attr("transform", ({ x, y }) => `translate(${x},${y})`)

    bubbles.append("title").text((d) => {
      // @ts-expect-error correct typings
      const [voter] = d.parent.data

      return `Address: ${voter.address}`
    })

    bubbles
      .append("circle")
      .attr("r", ({ r }) => r)
      .style("fill", (d) => {
        // @ts-expect-error correct typings
        const [support] = map(d.data, (value) => value)
        const { color } = voteStyles[support as string]

        return color
      })
      .style("cursor", "pointer")
      .on("click", handleVoterClick)

    bubbles
      .append("text")
      .attr("dy", ".2em")
      .style("text-anchor", "middle")
      .text((d) => {
        const username = getUsername(d, dataset)

        return clipText(d, username, 8)
      })
      .attr("font-family", "sans-serif")
      .attr("font-size", ({ r }) => r / 5)
      .attr("fill", "white")
      .style("pointer-events", "auto")
      .style("cursor", "pointer")
      .on("click", handleVoterClick)

    select(self.frameElement).style("height", `${height}px`)

    const zoomed = (event: D3ZoomEvent<SVGSVGElement, unknown>) => {
      const eventTransform = event.transform.toString()
      container.attr("transform", eventTransform)

      // update text size
      bubbles.selectAll("text").text((d) => {
        const username = getUsername(d as HierarchyCircularNode<Vote>, dataset)
        const fontSize = 12

        return clipText(
          d as HierarchyCircularNode<Vote>,
          username,
          fontSize / event.transform.k / 3,
        )
      })
    }

    const d3zoom = zoom<SVGSVGElement, unknown>()
      .translateExtent([
        [0, 0],
        [width, height],
      ])
      .scaleExtent([-10, 100])
      .on("zoom", zoomed)

    svgElement.call(d3zoom)

    const handleReset = () => {
      svgElement.call(d3zoom.transform, zoomIdentity.scale(1))
    }
    const buttonElement = buttonRef.current

    if (buttonElement !== null) {
      buttonElement.onclick = handleReset
    }

    const zoomInBtnElement = zoomInBtn.current
    const zoomOutBtnElement = zoomOutBtn.current

    const handleZoomIn = () => {
      d3zoom.scaleBy(svgElement, 1.2)
    }

    if (zoomInBtnElement !== null) {
      zoomInBtnElement.onclick = handleZoomIn
    }

    const handleZoomOut = () => {
      d3zoom.scaleBy(svgElement, 0.8)
    }

    if (zoomOutBtnElement !== null) {
      zoomOutBtnElement.onclick = handleZoomOut
    }
  }, [dataset, handleVoterClick, decimals, voteStyles])

  return {
    ref,
    buttonRef,
    zoomInBtn,
    zoomOutBtn,
  }
}
